interface PayoutRateCalculations {
  chargeRate: number;
  margin_percent?: number | string;
  charge_max_daily_regular_hours: number;
  charge_max_daily_ot_hours: number;
  charge_overtime_multiplier: number;
  payout_max_daily_regular_hours: number;
  payout_max_daily_ot_hours: number;
  payout_overtime_multiplier: number;
  charge_double_overtime_multiplier: number;
  payout_double_overtime_multiplier: number;
  hours: number;
  excludeTenCentReduction?: boolean;
}

type ChargeHoursParams = Pick<
  PayoutRateCalculations,
  | 'hours'
  | 'charge_max_daily_regular_hours'
  | 'charge_max_daily_ot_hours'
  | 'charge_overtime_multiplier'
  | 'charge_double_overtime_multiplier'
>;

type PayHoursParams = Pick<
  PayoutRateCalculations,
  | 'hours'
  | 'payout_max_daily_regular_hours'
  | 'payout_max_daily_ot_hours'
  | 'payout_overtime_multiplier'
  | 'payout_double_overtime_multiplier'
>;

type MarginCalculations = Omit<PayoutRateCalculations, 'margin_percent'> & {
  payoutRate: number;
};

export const calcChargeHours = ({
  hours,
  charge_max_daily_regular_hours,
  charge_max_daily_ot_hours,
  charge_overtime_multiplier,
  charge_double_overtime_multiplier,
}: ChargeHoursParams): number => {
  const regularHours = Math.min(hours, charge_max_daily_regular_hours);
  const overtimeHours =
    Math.max(hours - charge_max_daily_regular_hours, 0) * charge_overtime_multiplier;
  const doubleOvertimeHours =
    Math.max(hours - charge_max_daily_regular_hours - charge_max_daily_ot_hours, 0) *
    charge_double_overtime_multiplier;

  return regularHours + overtimeHours + doubleOvertimeHours;
};

export const calcPayHours = ({
  hours,
  payout_max_daily_regular_hours,
  payout_max_daily_ot_hours,
  payout_overtime_multiplier,
  payout_double_overtime_multiplier,
}: PayHoursParams): number => {
  const regularHours = Math.min(hours, payout_max_daily_regular_hours);
  const overtimeHours =
    Math.max(hours - payout_max_daily_regular_hours, 0) * payout_overtime_multiplier;
  const doubleOvertimeHours =
    Math.max(hours - payout_max_daily_regular_hours - payout_max_daily_ot_hours, 0) *
    payout_double_overtime_multiplier;

  return regularHours + overtimeHours + doubleOvertimeHours;
};

export const calcPayoutRate = (params: PayoutRateCalculations) => {
  const {
    chargeRate = 0,
    margin_percent = 0,
    hours = 0,
    charge_max_daily_regular_hours = 0,
    charge_max_daily_ot_hours = 0,
    charge_overtime_multiplier = 0,
    payout_max_daily_regular_hours = 0,
    payout_max_daily_ot_hours = 0,
    payout_overtime_multiplier = 0,
    payout_double_overtime_multiplier = 0,
    charge_double_overtime_multiplier = 0,
    excludeTenCentReduction = false,
  } = params;

  const normalizedMargin = Number(margin_percent || 0);

  const chargeHours = calcChargeHours({
    hours,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours,
    charge_overtime_multiplier,
    charge_double_overtime_multiplier,
  });

  const payHours = calcPayHours({
    hours,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  });

  if (!payHours) {
    return {
      payoutRate: 0,
      chargeHours,
      payHours,
    };
  }
  const tenCentReduction = excludeTenCentReduction ? 0 : 10;
  const totalPay = chargeHours * (chargeRate * (1 - normalizedMargin) - tenCentReduction);

  return {
    payoutRate: Math.round(totalPay / payHours),
    chargeHours,
    payHours,
  };
};

export const calc1099Margin = (params: MarginCalculations) => {
  const { chargeRate, payoutRate } = params;

  const chargeHours = calcChargeHours({
    hours: params.hours,
    charge_max_daily_regular_hours: params.charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: params.charge_max_daily_ot_hours,
    charge_overtime_multiplier: params.charge_overtime_multiplier,
    charge_double_overtime_multiplier: params.charge_double_overtime_multiplier,
  });

  const payHours = calcPayHours({
    hours: params.hours,
    payout_max_daily_regular_hours: params.payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: params.payout_max_daily_ot_hours,
    payout_overtime_multiplier: params.payout_overtime_multiplier,
    payout_double_overtime_multiplier: params.payout_double_overtime_multiplier,
  });

  const totalCharge = chargeRate * chargeHours;
  const totalPay = (payoutRate - 10) * payHours;

  if (!totalCharge || !totalPay) {
    return 0;
  }

  return Math.round((1 - totalPay / totalCharge) * 100000) / 100000.0;
};

export const calcTotalPay = (params: PayoutRateCalculations) => {
  const { payout_max_daily_regular_hours = 0, payout_overtime_multiplier = 0, hours = 0 } = params;

  const { payoutRate } = calcPayoutRate(params);
  const regularPay = payoutRate * Math.min(payout_max_daily_regular_hours, hours);
  const overtimePay =
    Math.max(hours - payout_max_daily_regular_hours, 0) *
    payoutRate *
    Number(payout_overtime_multiplier);

  return regularPay + overtimePay;
};

export const calcTotalCharge = (
  chargeRate: number = 0,
  hours: number = 0,
  charge_max_daily_regular_hours: number = 0,
  charge_overtime_multiplier: number = 0,
) => {
  const regularCharge = chargeRate * Math.min(charge_max_daily_regular_hours, hours);
  const overtimeCharge =
    Math.max(hours - charge_max_daily_regular_hours, 0) *
    chargeRate *
    Number(charge_overtime_multiplier);

  return regularCharge + overtimeCharge;
};
